import { z } from 'zod';
import type { BrandInput } from './generate';

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/);

/**
 * `config.brand_site.theme` as HO admin saves it and the website reads it from `GET /storefront/site`. Same keys and values
 * as the server's `BrandTheme` check (Pos.Menu), which also refuses a primary colour under 4.5:1 against white.
 */
export const brandThemeSchema = z.strictObject({
  primary: hex.optional(),
  accent: hex.optional(),
  neutralTint: z.enum(['cool', 'warm', 'pure']).optional(),
  radius: z.enum(['sharp', 'soft', 'round']).optional(),
  fontPair: z.enum(['be-vietnam-pro', 'inter', 'nunito', 'roboto-slab+inter']).optional(),
  darkMode: z.enum(['auto', 'off']).optional(),
});

export type BrandTheme = z.infer<typeof brandThemeSchema>;

/** The approved, self-hosted font pairs, all with full Vietnamese support. */
export const FONT_PAIRS: Record<NonNullable<BrandTheme['fontPair']>, { heading: string; body: string }> = {
  'be-vietnam-pro': { heading: '"Be Vietnam Pro", system-ui, sans-serif', body: '"Be Vietnam Pro", system-ui, sans-serif' },
  inter: { heading: 'Inter, system-ui, sans-serif', body: 'Inter, system-ui, sans-serif' },
  nunito: { heading: 'Nunito, system-ui, sans-serif', body: 'Nunito, system-ui, sans-serif' },
  'roboto-slab+inter': { heading: '"Roboto Slab", Georgia, serif', body: 'Inter, system-ui, sans-serif' },
};

/** Used when a site has no primary colour yet: a neutral slate that passes AA with white, never a brand's colour. */
export const DEFAULT_PRIMARY = '#334155';

/** The generator's input for a brand site: its name and theme, with the platform defaults for what the theme leaves out. */
export function fromBrandSite(siteName: string, theme: BrandTheme): BrandInput {
  return {
    name: siteName,
    primary: theme.primary ?? DEFAULT_PRIMARY,
    ...(theme.accent ? { accent: theme.accent } : {}),
    neutralTint: theme.neutralTint ?? 'warm',
    radius: theme.radius ?? 'soft',
    darkMode: theme.darkMode !== 'off',
    fonts: FONT_PAIRS[theme.fontPair ?? 'be-vietnam-pro'],
  };
}
